import { zodResolver } from '@hookform/resolvers/zod';
import {
  AccountFormInput,
  AccountSchema,
  AccountStatusEnum,
  AccountTypeEnum,
  ILegalEntityResponseSchema,
} from '@monetize/types/app';
import { DEFAULT_PAGER } from '@monetize/types/common';
import pick from 'lodash/pick';
import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useCreateAccount, useUpdateAccount } from '../../api/accountsService';
import { handleApiErrorToast } from '../../api/axios';
import { useGetListData } from '../../api/queryUtils';
import {
  ILegalEntitiesSelector,
  selectLegalEntities,
} from '../../api/selectors';
import { MBox, MButton, MCenterModal, MStack } from '../../components/Monetize';
import { ROUTES } from '../../constants';
import { useCurrencies } from '../../hooks/useCurrencies';
import { useFlags } from '../../services/launchDarkly';
import AccountForm from './AccountForm';

interface AccountFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  account?: any | null;
  isReadOnly?: boolean;
}

const AccountFormModal: FC<AccountFormModalProps> = ({
  isOpen,
  onClose,
  account: baseData,
  isReadOnly,
}: AccountFormModalProps) => {
  const { allowEditAccountAddress } = useFlags();
  const { defaultCurrency } = useCurrencies();
  const { data: legalEntitiesData } = useGetListData<
    ILegalEntityResponseSchema,
    ILegalEntitiesSelector
  >(
    'legalEntities',
    { config: DEFAULT_PAGER },
    {
      select: selectLegalEntities,
    },
  );

  const handleClose = () => {
    reset();
    onClose();
  };

  const { mutateAsync: doCreateAccount, isPending: isCreatingAccount } =
    useCreateAccount({
      onSuccess: (newAccount) => {
        handleClose();
        navigate(ROUTES.getAccountDetailRoute(newAccount.id));
      },
      onError: (error) => {
        handleApiErrorToast(error);
      },
    });

  const { mutateAsync: doUpdateAccount, isPending: isUpdatingAccount } =
    useUpdateAccount({
      onSuccess: (updatedAccount) => {
        handleClose();
      },
      onError: (error) => {
        handleApiErrorToast(error);
      },
    });

  const navigate = useNavigate();
  const formData = baseData
    ? pick<AccountFormInput>(baseData, Object.keys(AccountSchema.shape))
    : ({} as Partial<AccountFormInput>);
  const form = useForm<AccountFormInput>({
    resolver: zodResolver(AccountSchema),
    mode: 'onChange',
    defaultValues: {
      status: AccountStatusEnum.ACTIVE,
      defaultCurrency,
      isTestAccount: false,
      defaultLegalEntityId: legalEntitiesData?.defaultLegalEntity?.id,
      customFields: {},
      taxExemptionNumber: null,
      taxEntityUseCode: null,
      defaultDunningProcessId: null,
    },
    values: {
      defaultLegalEntityId: formData.defaultLegalEntityId
        ? formData.defaultLegalEntityId
        : legalEntitiesData?.defaultLegalEntity?.id!,
      defaultCurrency: formData.defaultCurrency
        ? formData.defaultCurrency
        : defaultCurrency,
      isTestAccount: formData.isTestAccount ? formData.isTestAccount : false,
      accountName: formData.accountName ? formData.accountName : '',
      status: formData.status ? formData.status : AccountStatusEnum.ACTIVE,
      customId: formData.customId ? formData.customId : null,
      customFields: formData.customFields ?? {},
      taxExemptionNumber: formData.taxExemptionNumber || null,
      taxEntityUseCode: formData.taxEntityUseCode || null,
      billingAddress: formData.billingAddress,
      shippingAddress: formData.shippingAddress,
      defaultDunningProcessId: formData.defaultDunningProcessId || null,
      ccEmails: formData.ccEmails || null,
      bccEmails: formData.bccEmails || null,
      accountType: formData.accountType
        ? formData.accountType
        : AccountTypeEnum.CUSTOMER,
    },
    resetOptions: {
      keepDirtyValues: true,
      keepErrors: true,
    },
  });
  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = form;

  const isValid = Object.keys(errors).length === 0;
  const onSubmit = async (data: AccountFormInput) => {
    try {
      // Parse and validate data with Zod schema
      const validatedData = AccountSchema.parse(data);

      if (baseData) {
        await doUpdateAccount({
          id: baseData.id,
          data: validatedData,
        });
      } else {
        await doCreateAccount(validatedData);
      }
    } catch (error) {
      // Handle validation errors or API errors
      handleApiErrorToast(error);
    }
  };

  return (
    <MCenterModal
      size="xl"
      isOpen={isOpen}
      onClose={handleClose}
      modalTitle={
        !baseData ? 'New Account' : `${!isReadOnly ? 'Edit' : 'View'} Account`
      }
      modalContentProps={{ 'data-testid': 'account-modal' } as any}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      renderFooter={
        isReadOnly
          ? null
          : () => (
              <MStack
                gap={4}
                direction="row"
                align="center"
                justify="end"
                flex={1}
              >
                <MButton onClick={handleClose} variant="cancel" minW="auto">
                  Cancel
                </MButton>
                <MButton
                  variant="primary"
                  form="account-form"
                  isLoading={isCreatingAccount || isUpdatingAccount}
                  onClick={handleSubmit(onSubmit)}
                  isDisabled={!isValid}
                  type="submit"
                  minW="auto"
                >
                  Save
                </MButton>
              </MStack>
            )
      }
    >
      <MBox>
        <form id="account-form" onSubmit={handleSubmit(onSubmit)}>
          <FormProvider {...form}>
            <AccountForm
              control={control}
              errors={errors}
              isReadOnly={isReadOnly}
              setValue={setValue}
              watch={watch}
              legalEntities={legalEntitiesData?.legalEntities || []}
              allowEditAccountAddress={allowEditAccountAddress}
            />
          </FormProvider>
        </form>
      </MBox>
    </MCenterModal>
  );
};

export default AccountFormModal;
